/**
 * Helpers for interpreting `reportHealth` docs on the dashboard (calendar glyphs, Activity day panel).
 */

import { format, isSameDay } from "date-fns";
import { normalizeFirestoreDate } from "./firestoreDate";

function isVisceralFatValid(value) {
  const v = Number(value);
  return Number.isFinite(v) && v > 0;
}

/** Meaningful synced fitness rows (Apple Health / HealthKit workouts) stored on daily health reports. */
export function reportHasSyncedFitnessActivities(report) {
  const acts = report?.reportData?.activities;
  return Array.isArray(acts) && acts.length > 0;
}

/** Weight, macros, HR, sleep-related tiles — excludes Apple Health workouts (see {@link reportHasSyncedFitnessActivities}). */
export function reportHasHealthMetricsExcludingSyncedFitness(report) {
  if (!report?.reportData) return false;
  const rd = report.reportData;
  return (
    (rd.weight != null && Number(rd.weight) > 0) ||
    (rd.steps != null && Number(rd.steps) > 0) ||
    Number(rd.carbs) > 0 ||
    Number(rd.proteins) > 0 ||
    Number(rd.fats) > 0 ||
    (rd.water != null && Number(rd.water) > 0) ||
    (rd.averageHeartRate != null && Number(rd.averageHeartRate) > 0) ||
    (rd.fatPercentage != null && Number(rd.fatPercentage) > 0) ||
    (rd.hydrationPercentage != null && Number(rd.hydrationPercentage) > 0) ||
    (rd.metabolicAge != null && Number(rd.metabolicAge) > 0) ||
    (rd.muscleMass != null && Number(rd.muscleMass) > 0) ||
    isVisceralFatValid(rd.visceralFat) ||
    (rd.waist != null && Number(rd.waist) > 0)
  );
}

/**
 * Calendar day stamp for health reports — prefer indexed day (doc id / `dateIndex`), not sync time.
 */
export function healthReportCalendarYmd(report) {
  if (!report) return null;
  const di = report.dateIndex;
  if (typeof di === "string" && /^\d{4}-\d{2}-\d{2}/.test(di)) return di.slice(0, 10);
  if (typeof report.id === "string" && /^\d{4}-\d{2}-\d{2}$/.test(report.id)) return report.id;
  return null;
}

/**
 * Includes reports saved only with `createdOn` (legacy) by falling back to the sync timestamp day.
 */
export function healthReportBelongsToCalendarDay(report, date) {
  if (!date || !report) return false;
  const ymd = format(date, "yyyy-MM-dd");
  const byIndex = healthReportCalendarYmd(report);
  if (byIndex) return byIndex === ymd;
  const d = normalizeFirestoreDate(report.createdOn);
  return Boolean(d && isSameDay(d, date));
}

/** Any tiles worth showing Health tab / calendar glyphs (training program + Apple Health workouts + vitals macros). */
export function reportHasMeaningfulHealthData(report) {
  return reportHasSyncedFitnessActivities(report) || reportHasHealthMetricsExcludingSyncedFitness(report);
}
